import { ApiStatusError, ValidationError, NetworkError, NetworkErrorType } from '../types';
import { showErrorSnackbar, showAuthErrorSnackbar, showNetworkErrorSnackbar } from '@/components/error';

export enum ErrorUIStrategy {
  /** Default/ChatInput behavior-snackbars for auth/network, setError for server errors */
  CHATINPUT = 'CHATINPUT',
  /** Sidebar behavior - setError for auth/network/server errors */
  SIDEBAR = 'SIDEBAR',
  /** Only snackbars, never setError - for background operations */
  SNACKBAR_ONLY = 'SNACKBAR_ONLY'
}

interface ErrorHandlerOptions {
  setError?: (error: string) => void; // Optional for validation errors
  context?: string; // For logging context
  strategy?: ErrorUIStrategy; // How to handle different error types
}

/**
 * Main error handler that routes different error types to appropriate UI feedback
 * 
 * @param err - The error to handle
 * @param options - Configuration options for error handling
 */
export async function handleError(
  err: unknown, 
  options: ErrorHandlerOptions
): Promise<void> {
  const { setError, context = 'Operation', strategy = ErrorUIStrategy.CHATINPUT } = options;
  
  console.error(`❌ ${context} failed:`, err);
  
  // Determine error type and handle accordingly
  switch (true) {
    case err instanceof ValidationError:
      handleValidationError(err, setError, strategy);
      break;
      
    case err instanceof NetworkError:
      handleNetworkError(err, setError, strategy);
      break;
      
    case err instanceof ApiStatusError:
      handleApiStatusError(err, setError, strategy);
      break;
      
    default:
      handleGenericError(err, setError, strategy, options);
      break;
  }
}

// Separate handler functions for clarity
function handleValidationError(
  err: ValidationError, 
  setError: ((error: string) => void) | undefined, 
  strategy: ErrorUIStrategy
): void {
  switch (strategy) {
    case ErrorUIStrategy.SIDEBAR:
      if (setError) {
        setError(err.message);
      } else {
        showNetworkErrorSnackbar(err.message);
      }
      break;

    case ErrorUIStrategy.SNACKBAR_ONLY:
      showNetworkErrorSnackbar(err.message);
      break;

    default:
      showNetworkErrorSnackbar(err.message);
      break;
  }
}

function handleNetworkError(
  err: NetworkError, 
  setError: ((error: string) => void) | undefined, 
  strategy: ErrorUIStrategy
): void {
  if (strategy === ErrorUIStrategy.SIDEBAR) {
    if (setError) {
      setError(err.getUserMessage());
    } else {
      showNetworkErrorSnackbar(err.getUserMessage());
    }
  } else {
    showNetworkErrorSnackbar(err.getUserMessage());
  }
}

function handleApiStatusError(
  err: ApiStatusError, 
  setError: ((error: string) => void) | undefined, 
  strategy: ErrorUIStrategy
): void {
  if (err.isAuthError()) {
    handleAuthError(err, setError, strategy);
  } else if (err.isServerError()) {
    handleServerError(err, setError, strategy);
  } else if (err.isClientError()) {
    handleClientError(err);
  }
}

function handleAuthError(
  err: ApiStatusError, 
  _setError: ((error: string) => void) | undefined, 
  _strategy: ErrorUIStrategy
): void {
  showAuthErrorSnackbar(err.detail || err.message);
}

function handleServerError(
  err: ApiStatusError, 
  setError: ((error: string) => void) | undefined, 
  strategy: ErrorUIStrategy
): void {
  switch (strategy) {
    case ErrorUIStrategy.SNACKBAR_ONLY:
      showErrorSnackbar(err.detail || err.message, err.traceId, err.instance);
      break;
      
    case ErrorUIStrategy.SIDEBAR:
        console.log("Sidebar error:", err);
        showErrorSnackbar(err.detail || err.message, err.traceId, err.instance);
      break;
    case ErrorUIStrategy.CHATINPUT:
    default:
      // Both ChatInput and Sidebar prefer setError for server errors
      if (setError) {
        setError(err.getUserMessage());
      } else {
        showErrorSnackbar(err.detail || err.message, err.traceId, err.instance);
      }
      break;
  }
}

function handleClientError(err: ApiStatusError): void {
  // Client errors (4xx) always show as snackbar regardless of strategy
  showErrorSnackbar(err.detail || err.message, err.traceId, err.instance);
}

function handleGenericError(
  err: unknown, 
  setError: ((error: string) => void) | undefined, 
  strategy: ErrorUIStrategy,
  _options: ErrorHandlerOptions
): void {
  const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
  
  // Check for legacy error patterns
  if (err instanceof Error) {
    switch (true) {
      case err.message.includes('fetch') || 
           err.message.includes('network') || 
           err.message.includes('Failed to connect'): {
        // Convert to NetworkError and re-handle
        const networkError = new NetworkError(err.message, NetworkErrorType.CONNECTION, err);
        handleNetworkError(networkError, setError, strategy);
        return;
      }
        
      case err.message.includes('No access token') || 
           err.message.includes('Authentication required') || 
           err.message.includes('token expired'):
        // Handle as auth error - always use snackbar regardless of strategy
        showAuthErrorSnackbar();
        return;
    }
  }
  
  // Generic error handling
  switch (strategy) {
    case ErrorUIStrategy.SNACKBAR_ONLY:
      showErrorSnackbar(errorMessage);
      break;
    case ErrorUIStrategy.SIDEBAR:
    case ErrorUIStrategy.CHATINPUT:
    default:
      if (setError) {
        setError(errorMessage);
      } else {
        showErrorSnackbar(errorMessage);
      }
      break;
  }
}
